import clsx from 'clsx'
import React, { useState, useEffect, useMemo } from 'react'
import { RadioGroup } from '@headlessui/react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogCloseButton,
} from '@wartek-id/dialog-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'

import { assetUrl } from 'utils/url'

export type SurveyOptionType = 1 | 2 | 3 | 4 | 5

type SurveyDialogOptionLabels = Record<SurveyOptionType, string>

const DEFAULT_OPTION_LABELS: SurveyDialogOptionLabels = {
  1: 'Sangat Sulit',
  2: 'Sulit',
  3: 'Cukup',
  4: 'Mudah',
  5: 'Sangat Mudah',
}

const OPTION_ORDER: SurveyOptionType[] = [1, 2, 3, 4, 5]

const FORM_STEP_IMAGE_SRC = assetUrl('/images/data-konsol.png')

const SUCCESS_STEP_IMAGE_SRC = assetUrl(
  '/illustrations/img-file-document-edit.png'
)

const STYLES_UNSELECTED: Record<SurveyOptionType, string> = {
  1: 'bg-surface-critical-subdued text-critical',
  2: 'bg-surface-critical-subdued text-critical',
  3: 'bg-surface-warning-subdued text-yellow-700',
  4: 'bg-surface-success-subdued text-success',
  5: 'bg-surface-success-subdued text-success',
}

const STYLES_SELECTED: Record<SurveyOptionType, string> = {
  1: 'bg-critical text-white',
  2: 'bg-critical text-white',
  3: 'bg-yellow-600 text-white',
  4: 'bg-success text-white',
  5: 'bg-success text-white',
}

const DEFAULT_QUESTION_TEXT =
  'Dalam skala 1-5 seberapa mudah Anda menyelesaikan alur ini?'

const DEFAULT_SUBMIT_LABEL = 'Kirim'

const DEFAULT_SUCCESS_TITLE =
  'Ceritakan ke kami lebih lengkap saran dan masukan Anda tentang fitur dalam Pengelolaan Kinerja'

const DEFAULT_SUCCESS_CONFIRM_LABEL = 'Saya mengerti'
const DEFAULT_SUCCESS_CLOSE_LABEL = 'x'

const defaultSuccessCountdownText = (secondsRemaining: number) =>
  `Dalam ${secondsRemaining} detik Anda akan diarahkan ke Survei Kepuasan`

export type SurveyDialogProps = Omit<DialogProps, 'children'> & {
  formUrl?: string
  /**
   * When set to a positive number, opens `formUrl` and invokes `onClose` after
   * that many seconds. Omit or set to `0` to disable auto-redirect (user must
   * tap the button).
   */
  autoRedirectSeconds?: number
  formContent?: SurveyDialogFormContent
  successContent?: SurveyDialogSuccessContent
  onSubmit?: ({
    value,
    label,
  }: {
    label: string
    value: SurveyOptionType
  }) => void
  onClickOption?: (value: SurveyOptionType) => void
  onClickClose?: () => void
  onClose?: (value?: string) => void
  onOpenFormUrl?: () => void
}

export type SurveyDialogFormContent = {
  question?: string
  ctaLabel?: string
  optionLabels?: Partial<SurveyDialogOptionLabels>
}

export type SurveyDialogSuccessContent = {
  title?: string
  ctaLabel?: string
  ctaCloseLabel?: string
  getCountdownText?: (secondsRemaining: number) => string
}

export function SurveyDialog({
  formUrl,
  autoRedirectSeconds,
  formContent,
  successContent,
  onOpenFormUrl,
  onClose,
  onSubmit,
  onClickOption,
  ...props
}: SurveyDialogProps) {
  const [step, setStep] = useState<'FORM' | 'SUCCESS'>('FORM')

  // ----------------------------------------------------
  // Resolve form content
  // ----------------------------------------------------
  const question = formContent?.question ?? DEFAULT_QUESTION_TEXT
  const formCtaLabel = formContent?.ctaLabel ?? DEFAULT_SUBMIT_LABEL

  const mergedOptionLabels = useMemo(
    () => ({
      ...DEFAULT_OPTION_LABELS,
      ...formContent?.optionLabels,
    }),
    [formContent?.optionLabels]
  )

  // ----------------------------------------------------
  // Resolve success content
  // ----------------------------------------------------
  const successTitle = successContent?.title ?? DEFAULT_SUCCESS_TITLE
  const successCtaLabel =
    successContent?.ctaLabel ?? DEFAULT_SUCCESS_CONFIRM_LABEL
  const successCtaCloseLabel =
    successContent?.ctaCloseLabel ?? DEFAULT_SUCCESS_CLOSE_LABEL
  const getCountdownText =
    successContent?.getCountdownText ?? defaultSuccessCountdownText
  const hasFormUrl = !!formUrl

  const handleClose = (clickedText) => {
    onClose?.(clickedText)
  }

  // ----------------------------------------------------
  // Reset step when dialog is opened
  // ----------------------------------------------------
  useEffect(() => {
    if (props?.isOpen) {
      setStep('FORM')
    }
  }, [props.isOpen])

  return (
    <Dialog
      closeOnOverlayClick={false}
      size={step === 'SUCCESS' ? 'sm' : 'md'}
      onClose={onClose}
      {...props}
    >
      <DialogOverlay />
      <DialogContent>
        {step === 'FORM' && (
          <StepForm
            question={question}
            ctaLabel={formCtaLabel}
            optionLabels={mergedOptionLabels}
            onClickOption={onClickOption}
            onSubmit={(selectedValue) => {
              setStep('SUCCESS')
              onSubmit?.({
                value: selectedValue,
                label: DEFAULT_OPTION_LABELS[selectedValue],
              })
              if (!hasFormUrl) {
                handleClose('')
              }
            }}
          />
        )}
        {hasFormUrl && step === 'SUCCESS' && (
          <StepSuccess
            formUrl={formUrl}
            title={successTitle}
            ctaLabel={successCtaLabel}
            ctaCloseLabel={successCtaCloseLabel}
            getCountdownText={getCountdownText}
            autoRedirectSeconds={autoRedirectSeconds}
            onConfirm={() => {
              handleClose(successContent?.ctaLabel)
            }}
            onClose={(clickedText) => {
              handleClose(clickedText)
            }}
            onOpenFormUrl={onOpenFormUrl}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

type StepFormProps = {
  question: string
  ctaLabel: string
  optionLabels: SurveyDialogOptionLabels
  onSubmit?: (selectedValue: SurveyOptionType) => void
  onClickOption?: (value: SurveyOptionType) => void
}

function StepForm({
  question,
  ctaLabel,
  optionLabels,
  onSubmit,
  onClickOption,
}: StepFormProps) {
  const [selected, setSelected] = useState<SurveyOptionType>()

  const handleSubmit = () => {
    if (selected === undefined) {
      return
    }

    onSubmit?.(selected)
  }

  return (
    <>
      <img src={FORM_STEP_IMAGE_SRC} alt="" loading="lazy" className="w-full" />
      <div className="px-4 pt-4 pb-6 bg-white rounded-b-xl">
        <Text className="mb-2" variant="heading-sm" as="h2">
          {question}
        </Text>

        <div className="p-3 mb-6 rounded-lg bg-gray-10">
          <RadioGroup
            onChange={setSelected}
            value={selected}
            className="flex gap-4"
          >
            {OPTION_ORDER.map((optionValue) => {
              const labelId = `label_${optionValue}`
              return (
                <div
                  className="w-full space-y-2 text-center flex-grow-1"
                  key={optionValue}
                >
                  <RadioGroup.Option value={optionValue} as={React.Fragment}>
                    {({ checked, active }) => (
                      <div
                        className={clsx(
                          'p-4 border border-disabled shadow-sm rounded text-center cursor-pointer',
                          active &&
                            'outline outline-2 outline-offset-1 outline-blue-60',
                          checked
                            ? STYLES_SELECTED[optionValue]
                            : STYLES_UNSELECTED[optionValue]
                        )}
                        aria-labelledby={labelId}
                        onClick={() => onClickOption?.(optionValue)}
                      >
                        <Text
                          as="span"
                          variant="heading-md"
                          className="!text-inherit"
                        >
                          {optionValue}
                        </Text>
                      </div>
                    )}
                  </RadioGroup.Option>

                  <Text
                    as="span"
                    className="text-center"
                    id={labelId}
                    variant={
                      selected === optionValue ? 'body-sm-bold' : 'body-sm'
                    }
                  >
                    {optionLabels[optionValue]}
                  </Text>
                </div>
              )
            })}
          </RadioGroup>
        </div>

        <Button fullWidth size="lg" disabled={!selected} onClick={handleSubmit}>
          {ctaLabel}
        </Button>
      </div>
    </>
  )
}

type StepSuccessProps = {
  formUrl?: string
  title: string
  ctaLabel: string
  ctaCloseLabel?: string
  getCountdownText: (secondsRemaining: number) => string
  autoRedirectSeconds?: number
  onConfirm?: () => void
  onOpenFormUrl?: () => void
  onClose?: (clickedText?: string) => void
}

function StepSuccess({
  formUrl,
  title,
  ctaLabel,
  ctaCloseLabel,
  getCountdownText,
  autoRedirectSeconds,
  onConfirm,
  onOpenFormUrl,
  onClose,
}: StepSuccessProps) {
  const redirectSeconds =
    autoRedirectSeconds && autoRedirectSeconds > 0
      ? autoRedirectSeconds
      : undefined

  const [countdown, setCountdown] = useState(() => redirectSeconds ?? 0)

  const openForm = () => {
    if (formUrl) {
      window.open(formUrl, '_blank')
      onOpenFormUrl?.()
    }
  }

  const handleConfirm = () => {
    openForm()
    onConfirm?.()
  }

  useEffect(() => {
    if (!redirectSeconds) {
      return
    }

    setCountdown(redirectSeconds)

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          return 0
        }

        if (prev === 1) {
          openForm()
          onClose?.()
          return 0
        }

        return prev - 1
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectSeconds])

  return (
    <div className="p-6">
      <DialogCloseButton onClick={() => onClose(ctaCloseLabel)} />
      <img
        src={SUCCESS_STEP_IMAGE_SRC}
        alt=""
        width={84}
        height={84}
        className="mb-3"
      />

      <div className="space-y-4">
        <Text variant="display-sm" as="h2">
          {title}
        </Text>

        {redirectSeconds && countdown > 0 ? (
          <div className="flex gap-2 p-4 border rounded bg-cyan-5 border-blue-70">
            <Text className="!font-bold">{getCountdownText(countdown)}</Text>
          </div>
        ) : null}

        <Button color="blue" fullWidth size="lg" onClick={handleConfirm}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  )
}
